import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useThrottledCallback } from 'use-debounce';
import { RedoIcon } from 'assets/icons';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import {
  fetchMyClientsOrdersRequestSaga,
  getMyClientsOrdersFilters,
  resetMyClientsOrdersFilter,
  setMyClientsOrdersFilter,
  updateMyClientsOrdersFiltersAndRefetchSaga,
} from 'domains/order/Order.store';
import {
  DATE_FROM,
  DATE_TO,
  MyClientsOrderFilterAndSortFieldLocal,
  MyClientsOrdersFilterAndSortField,
} from 'domains/order/Order.types';
import {
  getBooleanFilterOptions,
  getEnhancedStatusLabels,
  getStatusBarLabels,
  getStatusFilterPlaceholders,
  getSupplierFilterOptions,
  MIN_SEARCH_LENGTH,
} from 'pages/MyOrdersPage/ClientsOrders/ClientsOrders.utils';
import { SRangePicker, SSelect, STabs } from 'pages/MyOrdersPage/ClientsOrders/Filter/ClientsOrdersFilter.styled';
import { MultiSelectFilter } from 'pages/MyOrdersPage/ClientsOrders/Filter/components';
import { FilterInputField } from 'pages/MyOrdersPage/ClientsOrders/Filter/components/FilterInputField';
import { handleRangeFilterChange } from 'pages/MyOrdersPage/orders.utils';
import { Flex, Icon, MarginBox, Tab, TabsTitle, Text, WhiteButton } from 'UI';

export const ClientsOrdersFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector(getMyClientsOrdersFilters);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const statusTabItems = getStatusBarLabels(t);

  const statusFilterSelection = getEnhancedStatusLabels(t, filters);
  const booleanOptions = getBooleanFilterOptions(t);
  const supplierOptions = getSupplierFilterOptions(t);

  const updateFilters = useCallback(
    (field: MyClientsOrderFilterAndSortFieldLocal, value: string, validate = false) => {
      if (validate && value && value.length < MIN_SEARCH_LENGTH) {
        return;
      }
      dispatch(setMyClientsOrdersFilter({ field, value }));
    },
    [dispatch],
  );

  const handleToggleFilter = useCallback(
    (field: MyClientsOrderFilterAndSortFieldLocal, value: string) => {
      updateFilters(field, filters.get(field) === value ? '' : value);
    },
    [filters, updateFilters],
  );

  const handleRefresh = useThrottledCallback(
    () => {
      if (isDelegationLoading) {
        return;
      }
      dispatch(fetchMyClientsOrdersRequestSaga({ page: 1 }));
    },
    1000,
    { leading: true, trailing: false },
  );

  return (
    <Flex direction={'column'} gap={15} padding={'0 0 16px 0'}>
      <Flex align={'center'} gap={30}>
        <Text type="heading4" disableGutter>
          {t('common.filters', 'Filters')}
        </Text>
        <MarginBox mt={3}>
          <Text
            type="heading5"
            color="white"
            displayStyle={'link'}
            disableGutter
            hoverUnderLine
            cursor={'pointer'}
            onClick={() => {
              dispatch(resetMyClientsOrdersFilter());
            }}
            data-cy={'reset-filters-button'}
          >
            {t('common.action.reset_filters', 'Reset filters')}
          </Text>
        </MarginBox>
      </Flex>
      <STabs
        onChange={(value: string) => {
          if (isDelegationLoading) {
            return;
          }
          // Use atomic saga for status filter to prevent race condition
          dispatch(
            updateMyClientsOrdersFiltersAndRefetchSaga({
              field: MyClientsOrdersFilterAndSortField.STATUS,
              value: filters.get(MyClientsOrdersFilterAndSortField.STATUS) === value ? '' : value,
            }),
          );
        }}
        activeKey={filters.get(MyClientsOrdersFilterAndSortField.STATUS) ?? ''}
      >
        {statusTabItems.map((item) => (
          <Tab tab={<TabsTitle data-cy={item['data-cy']}>{item.label}</TabsTitle>} key={item.key} />
        ))}
      </STabs>
      <Flex gap={15} wrap={'wrap'} justify={'flex-end'}>
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.ORDER_NUMBER)}
          placeholder={t('clients_order.order_number', 'Order ref.')}
          onChangeDebounced={(value) => updateFilters(MyClientsOrdersFilterAndSortField.ORDER_NUMBER, value, true)}
          data-cy={'filter-input-order-number'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.RPARTSTORE_ORDER_NUMBER)}
          placeholder={t('clients_order.rpartstore_order_number', 'RPartStore Order ref.')}
          onChangeDebounced={(value) =>
            updateFilters(MyClientsOrdersFilterAndSortField.RPARTSTORE_ORDER_NUMBER, value, true)
          }
          data-cy={'filter-input-rpartstore-order-number'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.SUPPLIER_ORDER_NUMBER)}
          placeholder={t('clients_order.supplier_order_number', 'External Order ref.')}
          onChangeDebounced={(value) =>
            updateFilters(MyClientsOrdersFilterAndSortField.SUPPLIER_ORDER_NUMBER, value, true)
          }
          data-cy={'filter-input-external-order-number'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.GARAGE_NAME)}
          placeholder={t('clients_order.buyer', 'Buyer')}
          onChangeDebounced={(value) => updateFilters(MyClientsOrdersFilterAndSortField.GARAGE_NAME, value, true)}
          data-cy={'filter-input-buyer'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.BUYER_TRESOR_CODE)}
          placeholder={t('clients_order.tresor_code', 'Tresor code')}
          onChangeDebounced={(value) => updateFilters(MyClientsOrdersFilterAndSortField.BUYER_TRESOR_CODE, value, true)}
          data-cy={'filter-input-buyer-tresor-code'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.BUYER_BIR_CODE)}
          placeholder={t('clients_order.bir_code', 'BIR code')}
          onChangeDebounced={(value) => updateFilters(MyClientsOrdersFilterAndSortField.BUYER_BIR_CODE, value, true)}
          data-cy={'filter-input-buyer-bir-code'}
        />
        <FilterInputField
          value={filters.get(MyClientsOrdersFilterAndSortField.BUYER_DMS_NUMBER)}
          placeholder={t('clients_order.dms_number', 'DMS number')}
          onChangeDebounced={(value) => updateFilters(MyClientsOrdersFilterAndSortField.BUYER_DMS_NUMBER, value, true)}
          data-cy={'filter-input-dms-number'}
        />
        <Flex minWidth={100} maxWidth={100} data-cy={'filter-select-urgent'}>
          <SSelect
            onChange={(value: string) => handleToggleFilter(MyClientsOrdersFilterAndSortField.URGENT, value)}
            allowClear
            styleLess
            placeholder={t('order.urgent', 'Urgent')}
            value={filters.get(MyClientsOrdersFilterAndSortField.URGENT)}
            options={booleanOptions}
            bordered
          />
        </Flex>
        <Flex minWidth={150} maxWidth={150}>
          <MultiSelectFilter
            filters={statusFilterSelection}
            updateFilters={(value: string) => handleToggleFilter(MyClientsOrdersFilterAndSortField.STATUS, value)}
            getPlaceholders={getStatusFilterPlaceholders}
          />
        </Flex>
        <Flex minWidth={150} maxWidth={150} data-cy={'filter-select-dms-acknowledged'}>
          <SSelect
            onChange={(value: string) => handleToggleFilter(MyClientsOrdersFilterAndSortField.DMS_ACKNOWLEDGED, value)}
            allowClear
            styleLess
            placeholder={t('clients_order.dms_acknowledged', 'DMS Ack.')}
            value={filters.get(MyClientsOrdersFilterAndSortField.DMS_ACKNOWLEDGED)}
            options={booleanOptions}
            bordered
          />
        </Flex>
        <Flex minWidth={150} maxWidth={200} data-cy={'filter-select-distributed-by'}>
          <SSelect
            onChange={(value: string) => handleToggleFilter(MyClientsOrdersFilterAndSortField.SUPPLIER_NAME, value)}
            allowClear
            styleLess
            placeholder={t('clients_order.supplier', 'Distributed by')}
            value={filters.get(MyClientsOrdersFilterAndSortField.SUPPLIER_NAME)}
            options={supplierOptions}
            bordered
          />
        </Flex>
        <Flex minWidth={300} maxWidth={300}>
          <SRangePicker
            onChange={(dateStrings) => handleRangeFilterChange(dateStrings, updateFilters)}
            value={[filters.get(DATE_FROM) as string, filters.get(DATE_TO) as string]}
          />
        </Flex>
        <WhiteButton
          shape={'round'}
          data-cy={'refresh-button'}
          icon={<Icon IconComponent={RedoIcon} size={14} mb={2} />}
          onClick={handleRefresh}
        >
          {t('common.refresh', 'Refresh')}
        </WhiteButton>
      </Flex>
    </Flex>
  );
};
